import React, { useState, useCallback } from 'react';
import {
  Lock, Unlock, RefreshCw, RotateCcw, ChevronDown, ChevronUp,
  ShieldAlert, Tag, Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

interface VaultItem {
  id: string
  platform: string
  author: string | null
  title: string | null
  body: string | null
  url: string | null
  fetched_at: string
  rule_tags: string[] | null
  source_name: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60_000)
  if (mins < 60)  return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

const PLATFORM_COLORS: Record<string, string> = {
  twitter:  'bg-sky-100 text-sky-800',
  reddit:   'bg-orange-100 text-orange-800',
  bluesky:  'bg-blue-100 text-blue-800',
  youtube:  'bg-red-100 text-red-800',
  rss:      'bg-green-100 text-green-800',
}

// ── PIN gate ──────────────────────────────────────────────────────────────────

function PinGate({ onUnlock }: { onUnlock: () => void }) {
  const [pin, setPin] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (pin.length !== 4) return
    setChecking(true)
    setError('')
    try {
      const { data, error: rpcErr } = await supabase.rpc('verify_parent_lock_pin', { p_pin: pin })
      if (rpcErr) throw rpcErr
      if (!data) {
        setError('Incorrect PIN. Please try again.')
        setPin('')
        return
      }
      onUnlock()
    } catch {
      setError('Could not verify PIN. Try again.')
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="flex flex-col items-center gap-6 py-10 text-center">
      <div className="rounded-full bg-[#1A2E4A]/10 p-4">
        <Lock className="h-8 w-8 text-[#1A2E4A]" />
      </div>
      <div>
        <p className="font-semibold text-base">Content Vault is locked</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-xs">
          Enter your 4-digit vault PIN to see content your rules have filtered out.
        </p>
      </div>
      <form onSubmit={submit} className="w-full max-w-[200px] space-y-3">
        <Input
          type="password"
          inputMode="numeric"
          pattern="\d{4}"
          maxLength={4}
          placeholder="••••"
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, ''))
            setError('')
          }}
          className="text-center text-xl tracking-[0.5em] font-mono"
          autoFocus
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
        <Button
          type="submit"
          className="w-full bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
          disabled={pin.length !== 4 || checking}
        >
          {checking ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : 'Unlock'}
        </Button>
      </form>
      <p className="text-[11px] text-muted-foreground max-w-xs">
        No PIN set yet? Go to <span className="font-medium">Settings → Family</span> to set one.
      </p>
    </div>
  )
}

// ── Vault item card ───────────────────────────────────────────────────────────

function VaultCard({
  item,
  onRestore,
}: {
  item: VaultItem
  onRestore: (id: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const platformColor = PLATFORM_COLORS[item.platform] ?? 'bg-muted text-muted-foreground'
  const displayName =
    item.platform === 'rss' ? item.source_name ?? 'RSS'
    : item.platform === 'twitter' ? 'X'
    : item.platform.charAt(0).toUpperCase() + item.platform.slice(1)

  const preview = (item.title ?? item.body ?? '').slice(0, 160)
  const full    = [item.title, item.body].filter(Boolean).join('\n\n')

  return (
    <Card className="border border-border/60">
      <CardContent className="p-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center flex-wrap gap-1.5">
            <Badge className={`text-[10px] py-0 px-1.5 ${platformColor}`}>
              {displayName}
            </Badge>
            {item.author && (
              <span className="text-[11px] text-muted-foreground font-medium">
                {item.platform === 'reddit' ? `u/${item.author}` : `@${item.author}`}
              </span>
            )}
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <Clock className="h-2.5 w-2.5" />
              {timeAgo(item.fetched_at)}
            </span>
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            {expanded
              ? <ChevronUp className="h-3.5 w-3.5" />
              : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>

        {/* Content preview */}
        <p className={`text-xs text-foreground/80 leading-relaxed ${expanded ? '' : 'line-clamp-2'}`}>
          {expanded ? full : preview}
        </p>

        {/* Rule tags */}
        {item.rule_tags && item.rule_tags.length > 0 && (
          <div className="flex items-center flex-wrap gap-1 mt-2">
            <Tag className="h-2.5 w-2.5 text-muted-foreground shrink-0" />
            {item.rule_tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-[10px] py-0 px-1.5">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-2.5">
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[#1A2E4A] hover:underline"
            >
              View original ↗
            </a>
          )}
          <button
            onClick={() => onRestore(item.id)}
            className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            Restore to feed
          </button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export const ContentVault: React.FC = () => {
  const { user } = useAuth()
  const [unlocked, setUnlocked]   = useState(false)
  const [hasPinSet, setHasPinSet] = useState<boolean | null>(null)
  const [items, setItems]         = useState<VaultItem[]>([])
  const [loading, setLoading]     = useState(false)
  const [vaultLock, setVaultLock] = useState(false)  // whether vault_lock (parent_lock) is on

  // Check if a PIN has been set at all
  React.useEffect(() => {
    if (!user?.id) return
    supabase
      .from('profiles')
      .select('pin_hash, parent_lock')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setHasPinSet(Boolean((data as any)?.pin_hash))
        setVaultLock(Boolean((data as any)?.parent_lock))
      })
  }, [user?.id])

  const loadItems = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    const { data, error } = await supabase
      .from('social_feed_items')
      .select('id, platform, author, title, body, url, fetched_at, rule_tags, source_name')
      .eq('user_id', user.id)
      .eq('is_filtered', true)
      .order('fetched_at', { ascending: false })
      .limit(200)
    setLoading(false)
    if (error) { toast.error('Could not load vault'); return }
    setItems((data ?? []) as VaultItem[])
  }, [user?.id])

  const handleUnlock = useCallback(() => {
    setUnlocked(true)
    loadItems()
  }, [loadItems])

  const handleRestore = async (id: string) => {
    const { error } = await supabase
      .from('social_feed_items')
      .update({ is_filtered: false, is_pending_review: false, rules_applied_at: null } as any)
      .eq('id', id)
      .eq('user_id', user!.id)
    if (error) { toast.error('Could not restore item'); return }
    setItems((prev) => prev.filter((i) => i.id !== id))
    toast.success('Item restored to your feed')
  }

  const handleLock = () => setUnlocked(false)

  // ── No PIN set at all: open without gate, but show set-PIN nudge ──────────
  if (hasPinSet === false) {
    return (
      <div className="space-y-4">
        <VaultHeader
          itemCount={items.length}
          loading={loading}
          locked={false}
          onLock={handleLock}
          onRefresh={loadItems}
          showLock={false}
        />
        {!unlocked ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm space-y-2">
            <p className="font-medium text-amber-900 flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 shrink-0" />
              Content Vault is unprotected
            </p>
            <p className="text-amber-800 text-xs">
              No vault PIN has been set. Go to <span className="font-medium">Settings → Family</span> to
              set one — then the vault will require a PIN before revealing filtered content.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-900 hover:bg-amber-100"
              onClick={() => { setUnlocked(true); loadItems() }}
            >
              Open vault anyway
            </Button>
          </div>
        ) : (
          <VaultList items={items} loading={loading} onRestore={handleRestore} onRefresh={loadItems} />
        )}
      </div>
    )
  }

  // ── PIN set + vault_lock on: require PIN ──────────────────────────────────
  if (hasPinSet && vaultLock && !unlocked) {
    return (
      <div className="space-y-4">
        <VaultHeader
          itemCount={null}
          loading={false}
          locked={true}
          onLock={handleLock}
          onRefresh={() => {}}
          showLock={false}
        />
        <PinGate onUnlock={handleUnlock} />
      </div>
    )
  }

  // ── PIN set but vault_lock off: open without PIN, but lock button visible ─
  if (!unlocked) {
    return (
      <div className="space-y-4">
        <VaultHeader
          itemCount={items.length}
          loading={loading}
          locked={false}
          onLock={handleLock}
          onRefresh={loadItems}
          showLock={hasPinSet === true}
        />
        <VaultList items={items} loading={loading} onRestore={handleRestore} onRefresh={loadItems} />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <VaultHeader
        itemCount={items.length}
        loading={loading}
        locked={false}
        onLock={handleLock}
        onRefresh={loadItems}
        showLock={hasPinSet === true}
      />
      <VaultList items={items} loading={loading} onRestore={handleRestore} onRefresh={loadItems} />
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function VaultHeader({
  itemCount,
  loading,
  locked,
  onLock,
  onRefresh,
  showLock,
}: {
  itemCount: number | null
  loading: boolean
  locked: boolean
  onLock: () => void
  onRefresh: () => void
  showLock: boolean
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2">
          {locked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4 text-muted-foreground" />}
          Content Vault
          {itemCount !== null && itemCount > 0 && (
            <Badge variant="secondary" className="text-xs">{itemCount}</Badge>
          )}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Everything your rules have filtered out — nothing is deleted, it's all here.
        </p>
      </div>
      <div className="flex items-center gap-2">
        {!locked && (
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        )}
        {showLock && !locked && (
          <Button size="sm" variant="outline" onClick={onLock} className="h-7 text-xs">
            <Lock className="h-3 w-3 mr-1" />
            Lock vault
          </Button>
        )}
      </div>
    </div>
  )
}

function VaultList({
  items,
  loading,
  onRestore,
  onRefresh,
}: {
  items: VaultItem[]
  loading: boolean
  onRestore: (id: string) => void
  onRefresh: () => void
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        <Unlock className="h-8 w-8 mx-auto mb-3 opacity-30" />
        <p className="font-medium">The vault is empty</p>
        <p className="text-xs mt-1">Nothing has been filtered out yet. Your rules are either off or haven't matched anything.</p>
        <Button size="sm" variant="outline" onClick={onRefresh} className="mt-4">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refresh
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <VaultCard key={item.id} item={item} onRestore={onRestore} />
      ))}
    </div>
  )
}

export default ContentVault
